import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { TechnologyChart, ChartFrame } from '@ole-ui/ole-ui-components';
import { Text, TextVariants } from '@patternfly/react-core';
import { getTopItems, pieChartcolorScale } from '../helpers';

function TechnologyChartsFrame({
  products,
  topics,
  productVocabulary,
  topicVocabulary,
}) {
  const { t } = useTranslation();

  const getLabel = (token, vocabulary, fallbackLabel) => {
    const vocabLabel = vocabulary?.find((v) => v.token === token);
    return vocabLabel ? vocabLabel.display_name : fallbackLabel || token;
  };

  const normalizeData = (dataObj, vocabulary) => {
    if (!dataObj) return null;
    return Object.entries(dataObj).map(([token, val]) => {
      if (val && typeof val === 'object' && val.value !== undefined) {
        return {
          label: val.label || getLabel(token, vocabulary),
          value: val.value,
        };
      }
      return {
        label: getLabel(token, vocabulary),
        value: val,
      };
    });
  };

  const formattedProducts = React.useMemo(
    () => normalizeData(products, productVocabulary),
    [products, productVocabulary],
  );
  const formattedTopics = React.useMemo(
    () => normalizeData(topics, topicVocabulary),
    [topics, topicVocabulary],
  );

  const topProducts = React.useMemo(
    () => getTopItems(formattedProducts),
    [formattedProducts],
  );
  const topTopics = React.useMemo(
    () => getTopItems(formattedTopics),
    [formattedTopics],
  );

  const notAvailbleElement = (
    <Text component={TextVariants.p}>{t('No data available')}</Text>
  );

  let productChart = notAvailbleElement;
  let isLoadingProducts = true;

  let topicChart = notAvailbleElement;
  let isLoadingTopics = true;

  if (topProducts) {
    isLoadingProducts = false;
    const data = topProducts.map((product) => ({
      x: product.label,
      y: product.value,
    }));

    const legendData = topProducts.map((product) => ({
      name: product.label || '',
    }));

    if (data?.length > 0) {
      productChart = (
        <div className="chart-fade-in">
          <Text className="chart-print-title" component={TextVariants.h5}>
            {t('Product')}
          </Text>
          <TechnologyChart
            ariaDesc={t(
              'A pie chart showing the distribution of Red Hat products learned in your organization',
            )}
            data={data}
            colorScale={pieChartcolorScale}
            legendData={legendData}
          />
        </div>
      );
    }
  }

  if (topTopics) {
    isLoadingTopics = false;
    const data = topTopics.map((topic) => ({
      x: topic.label,
      y: topic.value,
    }));

    const legendData = topTopics.map((topic) => ({
      name: topic.label || '',
    }));

    if (data?.length > 0) {
      topicChart = (
        <div className="chart-fade-in">
          <Text className="chart-print-title" component={TextVariants.h5}>
            {t('Topics')}
          </Text>
          <TechnologyChart
            ariaDesc={t(
              'A pie chart showing the distribution of Red Hat technology topics learned in your organization',
            )}
            data={data}
            colorScale={pieChartcolorScale}
            legendData={legendData}
          />
        </div>
      );
    }
  }

  const popoverContent = (
    <>
      <p>
        <b>{t('Products:')}</b> {t('Red Hat Products')}
      </p>
      <p>
        <b>{t('Topics:')}</b> {t('IT Technology Topics')}
      </p>
    </>
  );

  return (
    <ChartFrame
      subTitle={t('Courses by technology')}
      popoverHeader={<div>{t('Courses by technology')}</div>}
      popoverContent={popoverContent}
      tabs={[
        {
          key: 'product',
          title: 'Product',
          chart: productChart,
          isLoading: isLoadingProducts,
        },
        {
          key: 'topics',
          title: 'Topics',
          chart: topicChart,
          isLoading: isLoadingTopics,
        },
      ]}
    />
  );
}

TechnologyChartsFrame.propTypes = {
  products: PropTypes.object,
  topics: PropTypes.object,
  productVocabulary: PropTypes.array,
  topicVocabulary: PropTypes.array,
};

TechnologyChartsFrame.defaultProps = {
  products: null,
  topics: null,
  productVocabulary: [],
  topicVocabulary: [],
};

export default TechnologyChartsFrame;
